import React, { useEffect, useRef, useState } from 'react';
import { Box, Grid2, TextField, Card, CardContent } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { showsAutocomplete } from '../autocomplete';
import AutocompleteSelect from './atom/AutoCompleteSelect';
import useNotify from '../hooks/useNotify';
import CommonButton from './atom/CommonButton';
import { useTranslation } from 'react-i18next';
import TimeslotCalendar from './TimeslotCalendar';
import { useGetTimeslots } from '@openapi/timeslots/timeslots';
import { useGetShow } from '@openapi/shows/shows';
import { useCapabilities } from '../hooks/useCapabilities';
import { getSlotTemplate } from '../helpers/slotTemplate';

function ShowTimeslotPicker({ baseUrl, onSelect }) {
    const { t } = useTranslation();
    const { notifyError } = useNotify();
    const navigate = useNavigate();
    const { can, CAPABILITIES } = useCapabilities();
    const canAssignSeats = can(CAPABILITIES.SEATING_ASSIGNMENTS);
    const [selectedShowId, setSelectedShowId] = useState(null);
    const [timeslots, setTimeslots] = useState([]);

    const { data: selectedShow } = useGetShow(Number(selectedShowId), {
        query: { enabled: !!selectedShowId },
    });
    const delayedSeatAssignmentEnabled = !!selectedShow?.marketing?.delayedSeatAssignmentEnabled;

    const {
        isLoading: loadingTimeslots,
        refetch: refetchTimeslots,
        error: fetchError,
        data: timeslotsData,
    } = useGetTimeslots(
        {
            pageSize: 10000,
            optionalId: selectedShowId,
            sort: 'starttime',
            sortDir: 'ASC',
            includeBlocks: true,
        },
        {
            query: {
                enabled: !!selectedShowId,
            },
        },
    );
    useEffect(() => {
        if (timeslotsData?.timeslots) {
            setTimeslots(timeslotsData.timeslots);
        }
    }, [timeslotsData]);
    useEffect(() => {
        if (selectedShowId) {
            refetchTimeslots();
        } else {
            setTimeslots([]);
        }
    }, [selectedShowId, refetchTimeslots]);

    useEffect(() => {
        if (fetchError) {
            setTimeslots([]);
            notifyError(fetchError);
        }
    }, [fetchError, notifyError]);

    const autoSelectDoneRef = useRef(false);
    const handleShowSelect = (showId) => {
        autoSelectDoneRef.current = true;
        setSelectedShowId(showId);
        setTimeslots([]);
    };

    const onSubmit = (timeslotId) => {
        if (!selectedShowId) return;

        if (onSelect) {
            onSelect(timeslotId, selectedShowId);
        } else {
            navigate(`${baseUrl}/${timeslotId}`);
        }
    };

    // Mount-only default: skip if the user has already picked something; swallow errors (default is non-critical).
    useEffect(() => {
        let mounted = true;
        showsAutocomplete
            .search('', 2)
            .then((opts) => {
                if (!mounted || autoSelectDoneRef.current) return;
                autoSelectDoneRef.current = true;
                if (opts.length === 1) {
                    setSelectedShowId(opts[0].id);
                }
            })
            .catch((err) => {
                console.error('Default show lookup failed', err);
            });
        return () => {
            mounted = false;
        };
    }, []);

    return (
        <Card variant="outlined">
            <CardContent>
                <Grid2 container spacing={3}>
                    <Grid2 size={{ xs: 12 }}>
                        <AutocompleteSelect
                            initCall={showsAutocomplete.init}
                            autocompleteCall={showsAutocomplete.search}
                            value={selectedShowId}
                            setValue={(showId) => {
                                handleShowSelect(showId);
                            }}
                        >
                            <TextField label={t('shows.title')} required fullWidth />
                        </AutocompleteSelect>
                    </Grid2>
                </Grid2>
                <Grid2 size={{ xs: 12 }}>
                    <TimeslotCalendar
                        timeslots={timeslots}
                        disabled={!selectedShowId || loadingTimeslots}
                        onTimeslotSelect={onSubmit}
                        onSeatAssign={
                            canAssignSeats && delayedSeatAssignmentEnabled
                                ? (timeslotId) =>
                                      navigate(
                                          `/shows/${selectedShowId}/timeslots/${timeslotId}/assign-seats`,
                                      )
                                : undefined
                        }
                        slotTemplate={getSlotTemplate(selectedShow)}
                    />
                </Grid2>
            </CardContent>
        </Card>
    );
}

export default ShowTimeslotPicker;
